// WebSocket hook for real-time updates
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';

interface UseWebSocketOptions {
  plannerPageId: string | null;
  enabled?: boolean;
}

export function useWebSocket({ plannerPageId, enabled = true }: UseWebSocketOptions) {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!enabled || !plannerPageId) return;

    try {
      // Use the same API URL as the axios instance
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const socket = io(apiUrl, {
        transports: ['websocket', 'polling'], // Fallback to polling if websocket fails
        withCredentials: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
      });

      socket.on('connect', () => {
        console.log('🔌 WebSocket connected');
        socket.emit('join_planner', plannerPageId);
      });

      socket.on('disconnect', () => {
        console.log('🔌 WebSocket disconnected');
      });

      socket.on('task_created', (task: any) => {
        console.log('📋 Task created:', task);
        queryClient.invalidateQueries({ queryKey: ['tasks', plannerPageId] });
      });

      socket.on('task_updated', (task: any) => {
        console.log('📋 Task updated:', task);
        queryClient.invalidateQueries({ queryKey: ['tasks', plannerPageId] });
        queryClient.invalidateQueries({ queryKey: ['task', task.id] });
      });

      socket.on('task_deleted', ({ taskId }: { taskId: string }) => {
        console.log('📋 Task deleted:', taskId);
        queryClient.invalidateQueries({ queryKey: ['tasks', plannerPageId] });
      });

      socket.on('task_moved', ({ task, oldStatus, newStatus }: any) => {
        console.log('📋 Task moved:', task.id, oldStatus, '->', newStatus);
        queryClient.invalidateQueries({ queryKey: ['tasks', plannerPageId] });
      });

      socket.on('comment_added', ({ taskId, comment }: any) => {
        console.log('💬 Comment added:', taskId);
        queryClient.invalidateQueries({ queryKey: ['task', taskId] });
        queryClient.invalidateQueries({ queryKey: ['tasks', plannerPageId] });
      });

      socketRef.current = socket;

      return () => {
        if (socket) {
          socket.emit('leave_planner', plannerPageId);
          socket.disconnect();
        }
      };
    } catch (error) {
      console.warn('⚠️  WebSocket not available - install socket.io-client:', error);
    }
  }, [plannerPageId, enabled, queryClient]);

  return socketRef.current;
}

